import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PropertyService from '../../service/PropertyService';
import { FiCheck, FiSearch } from 'react-icons/fi';
import {
  OnboardingShell,
  ErrorText,
  Group,
  SwitchRow,
  Counter,
  RequiredChoice,
} from 'components/Onboarding';
import {
  Layout,
  Side,
  MainCol,
  SearchWrap,
  GroupCard,
  AmenityChip,
  FollowUp,
  ReopenButton,
  SwitchStack,
} from './Amenities.styles';
import { groupAmenities, amenityLabel } from './amenityGroups';
import { getAmenityIcon } from './amenityIcons';
import { useDispatch, useSelector } from 'react-redux';
import {
  addAmenities,
  addAmenitiesObject,
  addOfferings,
  patchDetail,
  setPropertyId,
  updateOnboardingStage,
} from 'features/placeSlice';

import { notification } from 'antd';
import { useOnboardingBackConfirm } from 'library/hooks/useOnboardingBackConfirm';
import { petsNotAllowed } from 'library/helpers/petsAllowed';
import { isAdminUser } from 'library/utils/isAdmin';
import { discardListingDraft } from 'library/helpers/listingDraft';
import { reviewPathAfterEdit } from 'library/helpers/importEditFlow';
import {
  getApiResponseMessage,
  getUserFriendlyError,
} from 'library/helpers/errorMessage';

interface AmenityData {
  id: number;
  name: string;
  icon: string;
}

interface PropertyDetail {
  propertyDetailsId: string | number;
  detailsValue: number;
}

interface PropertyOffering {
  id: number;
  description: string;
}

interface LocationData {
  city: string;
  state: string;
  pincode: string;
  addressLine1: string;
  addressLine2: string;
  landmark: string;
  locality: string;
  latitude: number;
  longitude: number;
}

interface FinalAmenity {
  id: number;
  description: string;
}

interface PropertyDetailsPayload {
  propertyId?: string | null;
  title: string;
  description: string;
  bnbUrl: string;
  property3dUrl: string;
  uspsAndView: string;
  houseRules?: string;
  elderlyFriendly?: 'yes' | 'no';
  childFriendly?: 'yes' | 'no';
  petFriendly?: 'yes' | 'no';
  securityGuard24x7?: 'yes' | 'no';
  propertyStructureType?: 'standalone' | 'gated_complex';
  poolLength?: string;
  poolBreadth?: string;
  poolDepth?: string;
  parkingDescription?: string;
  flexibleCheckinType?: string;
  hostId: number;
  propertyTypeId: number;
  propertyTypeOther?: string | null;
  propertyOffering: PropertyOffering;
  details: PropertyDetail[];
  location: LocationData;
  amenities: FinalAmenity[];
  propertyMetadata: PropertyMetadataPayload;
  onboardingStage?: number;
}

interface PropertyMetadataPayload {
  uspsAndView: string;
  houseRules?: string;
  elderlyFriendly?: boolean;
  childFriendly?: boolean;
  petFriendly?: boolean;
  securityGuard24x7?: boolean;
  propertyStructureType?: 'standalone' | 'gated_complex';
  poolLength?: string;
  poolBreadth?: string;
  poolDepth?: string;
  parkingDescription?: string;
  flexibleCheckinType?: string;
}

interface AmenityLookup {
  id: number;
  name: string;
}

interface ReduxState {
  amenities: string[];
  amenitiesObject: AmenityData[];
  onboardingStage: number;
  detail: {
    title: string;
    description: string;
    bnbUrl: string;
    property3dUrl: string;
    uspsAndView: string;
    houseRules: string;
    elderlyFriendly: '' | 'yes' | 'no';
    childFriendly: '' | 'yes' | 'no';
    petFriendly: '' | 'yes' | 'no';
    securityGuard24x7: '' | 'yes' | 'no';
    propertyStructureType: '' | 'standalone' | 'gated_complex';
    poolLength: string;
    poolBreadth: string;
    poolDepth: string;
    parkingDescription: string;
    flexibleCheckinType: FlexibleCheckinFormValue;
  };
  place: {
    id: number;
    other?: string;
  };
  type: {
    id: number;
  };
  subType: {
    name: string;
  };
  offerings: Record<string, number>;
  location: {
    city: string;
    state: string;
    pincode: string;
    addressLine1: string;
    addressLine2: string;
    landmark: string;
    district: string;
    coordinates: {
      lat: string;
      lng: string;
    };
  };
}

interface ApiResponse {
  responseInformation: {
    responseCode: number;
  };
  result: {
    propertyId: string;
  };
}

// Maps amenity display name → icon filename when the backend returns the wrong/shared icon.
// Key: exact amenity name from API. Value: filename in /images/amenities/ (without .png).
const ICON_OVERRIDE: Record<string, string> = {
  'Kettle': 'kettle',
  'Shared Garden' : 'Shared garden'
};

const getIcon = (amenity: AmenityData): string =>
  ICON_OVERRIDE[amenity.name] ?? amenity.icon;

const normAmenity = (name: string): string => name.toLowerCase().trim();

/** Matches private pool, shared pool, swimming pool, etc. Excludes jacuzzi / hot tub. */
const isPoolAmenityName = (name: string): boolean => {
  const n = normAmenity(name);
  if (!n) return false;
  if (n.includes('jacuzzi')) return false;
  if (n.includes('hot tub') || n.includes('hot-tub') || n.includes('hottub')) {
    return false;
  }
  return (
    n.includes('pool') || n.includes('swimming') || n.includes('plunge')
  );
};

const isParkingAmenityName = (name: string): boolean => {
  const n = normAmenity(name);
  return (
    n.includes('parking') ||
    n.includes('car park') ||
    n.includes('carpark') ||
    n.includes('parking space')
  );
};

/**
 * Amenity follow-up for flexible check-in/out (e.g. API name "Flexi Checkin/Out").
 * Matches "flexi" or "flexible" plus check-in / check-out wording.
 */
const isFlexibleCheckinAmenityName = (name: string): boolean => {
  const n = normAmenity(name);
  const hasFlexLabel = n.includes('flexi') || n.includes('flexible');
  if (!hasFlexLabel) return false;
  return (
    n.includes('checkin') ||
    n.includes('check-in') ||
    n.includes('check in') ||
    n.includes('checkout') ||
    n.includes('check-out') ||
    n.includes('check out') ||
    n.includes('check/out')
  );
};

const FLEXIBLE_CHECKIN_VALUES = [
  'self',
  'assisted',
  'front_desk',
  'digital',
] as const;

type FlexibleCheckinFormValue = (typeof FLEXIBLE_CHECKIN_VALUES)[number] | '';

const amenitySelectionFlags = (selectedNames: string[]) => ({
  pool: selectedNames.some(isPoolAmenityName),
  parking: selectedNames.some(isParkingAmenityName),
  flexibleCheckin: selectedNames.some(isFlexibleCheckinAmenityName),
});

const yesNoToBoolean = (value?: '' | 'yes' | 'no'): boolean | undefined => {
  if (value === 'yes') return true;
  if (value === 'no') return false;
  return undefined;
};

const Amenities: React.FC = () => {
  let navigate = useNavigate();

  const [data, setData] = useState<AmenityData[]>([]);
  const { amenities, amenitiesObject, onboardingStage } = useSelector(
    (select: any) => select,
  ) as {
    amenities: string[];
    amenitiesObject: AmenityData[];
    onboardingStage: number;
  };
  const [features, setFeatures] = useState<string[]>([]);
  const [allFeatures, setAllFeatures] = useState<AmenityData[]>([]);
  const [followUpCollapsed, setFollowUpCollapsed] = useState({
    pool: false,
    parking: false,
    flexibleCheckin: false,
  });
  const [amenityGridCols, setAmenityGridCols] = useState(3);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 576px)');
    const sync = (): void => setAmenityGridCols(mq.matches ? 2 : 3);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);
  const dispatch = useDispatch();
  const finalResult = useSelector((state: any) => state) as ReduxState;
  const businessType = useSelector((state: any) => state.businessType ?? 'B2C') as 'B2B' | 'B2C';

  const buildDetailExtras = (): Pick<
    PropertyDetailsPayload,
    | 'uspsAndView'
    | 'houseRules'
    | 'elderlyFriendly'
    | 'childFriendly'
    | 'petFriendly'
    | 'securityGuard24x7'
    | 'propertyStructureType'
  > => {
    const d = finalResult?.detail;
    const extras: Pick<
      PropertyDetailsPayload,
      | 'uspsAndView'
      | 'houseRules'
      | 'elderlyFriendly'
      | 'childFriendly'
      | 'petFriendly'
      | 'securityGuard24x7'
      | 'propertyStructureType'
    > = {
      uspsAndView: d?.uspsAndView || '',
      houseRules: d?.houseRules || '',
    };
    if (d?.elderlyFriendly === 'yes' || d?.elderlyFriendly === 'no') {
      extras.elderlyFriendly = d.elderlyFriendly;
    }
    if (d?.childFriendly === 'yes' || d?.childFriendly === 'no') {
      extras.childFriendly = d.childFriendly;
    }
    if (d?.petFriendly === 'yes' || d?.petFriendly === 'no') {
      extras.petFriendly = d.petFriendly;
    }
    if (d?.securityGuard24x7 === 'yes' || d?.securityGuard24x7 === 'no') {
      extras.securityGuard24x7 = d.securityGuard24x7;
    }
    if (d?.propertyStructureType === 'standalone' || d?.propertyStructureType === 'gated_complex') {
      extras.propertyStructureType = d.propertyStructureType;
    }
    return extras;
  };

  const buildAmenityFollowUpExtras = (
    selectedNames: string[],
  ): Pick<
    PropertyDetailsPayload,
    | 'poolLength'
    | 'poolBreadth'
    | 'poolDepth'
    | 'parkingDescription'
    | 'flexibleCheckinType'
  > => {
    const d = finalResult?.detail;
    const sel = amenitySelectionFlags(selectedNames);
    const out: Pick<
      PropertyDetailsPayload,
      | 'poolLength'
      | 'poolBreadth'
      | 'poolDepth'
      | 'parkingDescription'
      | 'flexibleCheckinType'
    > = {};

    if (sel.pool) {
      if (d?.poolLength?.trim()) out.poolLength = d.poolLength.trim();
      if (d?.poolBreadth?.trim()) out.poolBreadth = d.poolBreadth.trim();
      if (d?.poolDepth?.trim()) out.poolDepth = d.poolDepth.trim();
    }
    if (sel.parking && d?.parkingDescription?.trim()) {
      out.parkingDescription = d.parkingDescription.trim();
    }
    if (
      sel.flexibleCheckin &&
      d?.flexibleCheckinType &&
      FLEXIBLE_CHECKIN_VALUES.includes(
        d.flexibleCheckinType as (typeof FLEXIBLE_CHECKIN_VALUES)[number],
      )
    ) {
      out.flexibleCheckinType = d.flexibleCheckinType;
    }
    return out;
  };

  const buildPropertyMetadata = (
    selectedNames: string[],
  ): PropertyMetadataPayload => {
    const detailExtras = buildDetailExtras();
    const amenityFollowUpExtras = buildAmenityFollowUpExtras(selectedNames);

    const metadata: PropertyMetadataPayload = {
      uspsAndView: detailExtras.uspsAndView || '',
      houseRules: detailExtras.houseRules || '',
      ...amenityFollowUpExtras,
    };

    const elderlyFriendly = yesNoToBoolean(detailExtras.elderlyFriendly);
    if (typeof elderlyFriendly === 'boolean') {
      metadata.elderlyFriendly = elderlyFriendly;
    }
    const childFriendly = yesNoToBoolean(detailExtras.childFriendly);
    if (typeof childFriendly === 'boolean') {
      metadata.childFriendly = childFriendly;
    }
    const petFriendly = yesNoToBoolean(detailExtras.petFriendly);
    if (typeof petFriendly === 'boolean') {
      metadata.petFriendly = petFriendly;
    }
    const securityGuard24x7 = yesNoToBoolean(detailExtras.securityGuard24x7);
    if (typeof securityGuard24x7 === 'boolean') {
      metadata.securityGuard24x7 = securityGuard24x7;
    }

    if (detailExtras.propertyStructureType) {
      metadata.propertyStructureType = detailExtras.propertyStructureType;
    }

    return metadata;
  };

  const getFinalAmenities = (): FinalAmenity[] => {
    const amenityLookupByName = new Map<string, AmenityLookup>();
    const d = finalResult?.detail;

    allFeatures.forEach((item) => {
      amenityLookupByName.set(item.name, { id: item.id, name: item.name });
    });

    data.forEach((item) => {
      if (!amenityLookupByName.has(item.name)) {
        amenityLookupByName.set(item.name, { id: item.id, name: item.name });
      }
    });

    return Array.from(new Set(features))
      .map((name) => amenityLookupByName.get(name))
      .filter((item): item is AmenityLookup => !!item)
      .map((item) => {
        let description = item.name;

        if (isPoolAmenityName(item.name)) {
          const parts: string[] = [];
          if (d?.poolLength?.trim()) parts.push(`Length: ${d.poolLength.trim()}`);
          if (d?.poolBreadth?.trim()) parts.push(`Width: ${d.poolBreadth.trim()}`);
          if (d?.poolDepth?.trim()) parts.push(`Depth: ${d.poolDepth.trim()}`);
          if (parts.length > 0) description = parts.join(', ');
        } else if (isParkingAmenityName(item.name)) {
          if (d?.parkingDescription?.trim()) description = d.parkingDescription.trim();
        } else if (isFlexibleCheckinAmenityName(item.name)) {
          if (
            d?.flexibleCheckinType &&
            FLEXIBLE_CHECKIN_VALUES.includes(
              d.flexibleCheckinType as (typeof FLEXIBLE_CHECKIN_VALUES)[number],
            )
          ) {
            description = d.flexibleCheckinType;
          }
        }

        return { id: item.id, description };
      });
  };

  const useQuery = (): URLSearchParams => {
    return new URLSearchParams(useLocation().search);
  };

  let query = useQuery();
  const hostPropertyId = query.get('id');
  const isEdit =
    hostPropertyId !== 'null' &&
    hostPropertyId !== null &&
    hostPropertyId !== undefined
      ? true
      : false;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [error]);

  // Self-hydrate amenity selections + follow-up details when arriving in edit mode.
  // Always runs on mount so previously-saved descriptions are never lost.
  useEffect(() => {
    if (!isEdit || !hostPropertyId) return;

    PropertyService.getPropertyDetailsById(hostPropertyId)
      .then((res) => {
        if ((res.responseInformation as any)?.responseCode !== 0) return;
        const serverAmenities: any[] = res.result?.amenities || [];
        const meta = (res.result as any)?.propertyMetadata || {};

        // Only overwrite amenity selections when Redux doesn't have them yet
        if (amenities.length === 0) {
          const names = serverAmenities.map((a: any) => a.name).filter(Boolean);
          dispatch(addAmenities(names));
          dispatch(addAmenitiesObject(serverAmenities));
        }

        // Restore follow-up details. propertyMetadata is the authoritative source
        // because updateProperty explicitly writes to it. amenities[].description
        // is a fallback for older records that predate metadata storage.
        const patches: Partial<typeof finalResult.detail> = {};

        // Primary: propertyMetadata
        if (meta.parkingDescription) patches.parkingDescription = String(meta.parkingDescription);
        if (meta.poolLength)  patches.poolLength  = String(meta.poolLength);
        if (meta.poolBreadth) patches.poolBreadth = String(meta.poolBreadth);
        if (meta.poolDepth)   patches.poolDepth   = String(meta.poolDepth);
        if (
          meta.flexibleCheckinType &&
          FLEXIBLE_CHECKIN_VALUES.includes(meta.flexibleCheckinType as any)
        ) {
          patches.flexibleCheckinType = meta.flexibleCheckinType as (typeof FLEXIBLE_CHECKIN_VALUES)[number];
        }

        // Fallback: parse amenities[].description for records without metadata
        serverAmenities.forEach((a: any) => {
          const name = String(a.name || '').toLowerCase();
          const desc = String(a.description || '');
          if (isPoolAmenityName(name) && !patches.poolLength) {
            const lenM = desc.match(/Length:\s*([^,]+)/i);
            const widM = desc.match(/Width:\s*([^,]+)/i);
            const depM = desc.match(/Depth:\s*([^,]+)/i);
            if (lenM) patches.poolLength = lenM[1].trim();
            if (widM) patches.poolBreadth = widM[1].trim();
            if (depM) patches.poolDepth = depM[1].trim();
          } else if (isParkingAmenityName(name) && !patches.parkingDescription) {
            if (desc && desc !== a.name) patches.parkingDescription = desc;
          } else if (isFlexibleCheckinAmenityName(name) && !patches.flexibleCheckinType) {
            const norm = desc.toLowerCase().replace(/[-\s]+/g, '_');
            if (FLEXIBLE_CHECKIN_VALUES.includes(norm as any)) {
              patches.flexibleCheckinType = norm as (typeof FLEXIBLE_CHECKIN_VALUES)[number];
            }
          }
        });

        // Last resort: localStorage cache written by updateHandler after a successful save.
        // Needed because updateStore (called from dashboard) resets Redux to initialState,
        // and the API may return propertyMetadata: null for Airbnb-imported listings.
        try {
          const cached = localStorage.getItem(`amenityFollowUp_${hostPropertyId}`);
          if (cached) {
            const c = JSON.parse(cached);
            if (!patches.parkingDescription && c.parkingDescription) patches.parkingDescription = c.parkingDescription;
            if (!patches.flexibleCheckinType && c.flexibleCheckinType && FLEXIBLE_CHECKIN_VALUES.includes(c.flexibleCheckinType as any)) {
              patches.flexibleCheckinType = c.flexibleCheckinType as (typeof FLEXIBLE_CHECKIN_VALUES)[number];
            }
            if (!patches.poolLength && c.poolLength) patches.poolLength = c.poolLength;
            if (!patches.poolBreadth && c.poolBreadth) patches.poolBreadth = c.poolBreadth;
            if (!patches.poolDepth && c.poolDepth) patches.poolDepth = c.poolDepth;
          }
        } catch (_) {}

        if (Object.keys(patches).length > 0) dispatch(patchDetail(patches));
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, hostPropertyId]);

  // The amenity list can fail to load (network hiccup, backend restarting). The service swallows
  // the error and returns an empty list, so retry a few times before telling the host.
  const [amenitiesFailed, setAmenitiesFailed] = useState(false);
  const [amenitiesReload, setAmenitiesReload] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setAmenitiesFailed(false);
    const load = async (): Promise<void> => {
      for (let attempt = 0; attempt < 4; attempt += 1) {
        try {
          const responseData = await PropertyService.getAmenities();
          const list = (responseData as any)?.result;
          if (Array.isArray(list) && list.length > 0) {
            if (!cancelled) setData(list);
            return;
          }
        } catch (error) {
          console.error('Error fetching data:', error);
        }
        if (cancelled) return;
        await new Promise((resolve) => setTimeout(resolve, 800 * (attempt + 1)));
      }
      if (!cancelled) setAmenitiesFailed(true);
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [amenitiesReload]);

  useEffect(() => {
    if (amenities.length > 0) {
      setFeatures(amenities);
      setAllFeatures(amenitiesObject);
    }
  }, [amenities, amenitiesObject]);

  const doBack = (): void => {
    dispatch(addAmenities(features));
    if (isEdit) {
      navigate(`/host/location?id=${hostPropertyId}`);
      return;
    }
    navigate('/host/location');
  };

  const { backHandler, BackConfirmModal } = useOnboardingBackConfirm(doBack);

  const updateHandler = async (): Promise<void> => {
    if (features.length === 0) {
      setError('Please select at least one amenity');
      return;
    }
    dispatch(addAmenities(features));
    dispatch(addAmenitiesObject(allFeatures));
    try {
      const finalAmenities = getFinalAmenities();
      const hostId = localStorage.getItem('userDetails')
        ? JSON.parse(localStorage.getItem('userDetails') || '{}').id
        : 0;

      let finalPropertyDetails: any;

      if (isEdit && hostPropertyId) {
        // Fetch live property data so we don't send undefined/empty required fields
        // when the host arrived directly at this page (Redux state is not fully populated)
        const fetchRes = await PropertyService.getPropertyDetailsById(hostPropertyId);
        if ((fetchRes.responseInformation as any)?.responseCode !== 0) {
          notification.error({ message: 'Unable to load property data. Please try again.' });
          return;
        }
        const prop = fetchRes.result as any;
        const loc = prop.location || {};

        // Strip query params from bnbUrl — the Airbnb import stores the full URL
        // with tracking params (260+ chars) but updateProperty validates max ~50 chars.
        const rawBnbUrl: string = prop.bnbUrl || '';
        let cleanBnbUrl = rawBnbUrl;
        try {
          if (rawBnbUrl) {
            const u = new URL(rawBnbUrl);
            cleanBnbUrl = `${u.protocol}//${u.hostname}${u.pathname}`;
          }
        } catch (_) {
          cleanBnbUrl = rawBnbUrl;
        }

        finalPropertyDetails = {
          propertyId: hostPropertyId,
          // Prefer Redux (user's in-session edits); fall back to API snapshot when Redux is empty.
          title: finalResult?.detail?.title || prop.title || '',
          description: finalResult?.detail?.description || prop.description || '',
          bnbUrl: cleanBnbUrl,
          property3dUrl: prop.property3dUrl || '',
          hostId: prop.hostId || hostId,
          propertyTypeId: finalResult?.place?.id || prop.propertyTypeId,
          propertyTypeOther: finalResult?.place?.other ?? prop.propertyTypeOther,
          propertyOffering: {
            id: finalResult?.type?.id || prop.propertyOffering?.id,
            description: finalResult?.subType?.name || prop.propertyOffering?.description || prop.propertyOffering?.name || '',
          },
          // Prefer Redux offerings (user's in-session edits from PropertyOfferings).
          // Fall back to the API snapshot only when Redux has no offerings yet
          // (e.g. host jumped directly to this page without going through the wizard).
          details: (() => {
            const reduxOfferings = finalResult?.offerings;
            const hasReduxOfferings =
              reduxOfferings != null && Object.keys(reduxOfferings).length > 0;
            if (hasReduxOfferings) {
              return Object.keys(reduxOfferings).map((key) => ({
                propertyDetailsId: key,
                detailsValue: reduxOfferings[key],
              }));
            }
            return (prop.details || []).map((d: any) => ({
              propertyDetailsId: d.propertyDetailsId,
              detailsValue: d.detailsValue,
            }));
          })(),
          location: {
            city: finalResult?.location?.city || loc.city || '',
            state: finalResult?.location?.state || loc.state || '',
            pincode: String(finalResult?.location?.pincode || loc.pincode || ''),
            addressLine1: finalResult?.location?.addressLine1 || loc.addressLine1 || '',
            addressLine2: finalResult?.location?.addressLine2 || loc.addressLine2 || '',
            landmark: finalResult?.location?.landmark || loc.landmark || '',
            locality: finalResult?.location?.district || loc.locality || loc.district || '',
            latitude: (() => {
              const v = finalResult?.location?.coordinates?.lat ?? loc.latitude;
              return typeof v === 'number' ? v : parseFloat(v || '0');
            })(),
            longitude: (() => {
              const v = finalResult?.location?.coordinates?.lng ?? loc.longitude;
              return typeof v === 'number' ? v : parseFloat(v || '0');
            })(),
          },
          amenities: finalAmenities,
          propertyMetadata: buildPropertyMetadata(features),
        };
      } else {
        finalPropertyDetails = {
          propertyId: hostPropertyId || localStorage.getItem('id'),
          title: finalResult?.detail?.title,
          description: finalResult?.detail?.description,
          bnbUrl: finalResult?.detail?.bnbUrl,
          property3dUrl: finalResult?.detail?.property3dUrl,
          hostId,
          propertyTypeId: finalResult?.place?.id,
          propertyTypeOther: finalResult?.place?.other || undefined,
          propertyOffering: {
            id: finalResult?.type?.id,
            description: finalResult?.subType?.name,
          },
          details: Object.keys(finalResult?.offerings).map((offering) => ({
            propertyDetailsId: offering,
            detailsValue: finalResult?.offerings[offering],
          })),
          location: {
            city: finalResult?.location?.city,
            state: finalResult?.location?.state,
            pincode: finalResult?.location?.pincode,
            addressLine1: finalResult?.location?.addressLine1,
            addressLine2: finalResult?.location?.addressLine2,
            landmark: finalResult?.location?.landmark,
            locality: finalResult?.location?.district,
            latitude: parseFloat(finalResult?.location?.coordinates?.lat),
            longitude: parseFloat(finalResult?.location?.coordinates?.lng),
          },
          amenities: finalAmenities,
          propertyMetadata: buildPropertyMetadata(features),
        };
      }

      const res = await PropertyService.updateProperty(finalPropertyDetails);

      if (
        res.responseInformation &&
        (res.responseInformation as any)?.responseCode === 0
      ) {
        // isEdit means the host jumped straight to this page to edit an existing property
        // (from the dashboard, not the linear onboarding wizard) — that's an update, so it
        // shouldn't get the onboarding-continuation copy meant for a brand new property.
        if (isEdit) {
          notification.success({
            message: 'Amenities updated',
            description: 'Your changes have been saved.',
          });
        }
        // Cache follow-up descriptions keyed by property — survives Redux resets
        // (updateStore wipes all state when returning from dashboard)
        const followUpToCache: Record<string, string> = {};
        if (finalResult.detail?.parkingDescription?.trim()) followUpToCache.parkingDescription = finalResult.detail.parkingDescription.trim();
        if (finalResult.detail?.flexibleCheckinType) followUpToCache.flexibleCheckinType = finalResult.detail.flexibleCheckinType;
        if (finalResult.detail?.poolLength?.trim()) followUpToCache.poolLength = finalResult.detail.poolLength.trim();
        if (finalResult.detail?.poolBreadth?.trim()) followUpToCache.poolBreadth = finalResult.detail.poolBreadth.trim();
        if (finalResult.detail?.poolDepth?.trim()) followUpToCache.poolDepth = finalResult.detail.poolDepth.trim();
        if (Object.keys(followUpToCache).length > 0) {
          localStorage.setItem(`amenityFollowUp_${hostPropertyId}`, JSON.stringify(followUpToCache));
        }
      } else {
        notification.error({
          message: 'Unable to update amenities',
          description: getApiResponseMessage(
            res,
            'Could not update amenities. Please try again.',
          ),
        });
        return;
      }
      if (res.result && res.result.propertyId) {
        dispatch(setPropertyId(res.result.propertyId));
      }
      navigate(reviewPathAfterEdit(hostPropertyId || localStorage.getItem('id')));
    } catch (e: any) {
      notification.error({
        message: 'Unable to update amenities',
        description: getUserFriendlyError(
          e,
          'Could not update amenities. Please try again.',
        ),
      });
      return;
    }
  };

  const nextHandler = async (
    e: React.MouseEvent<HTMLButtonElement>,
  ): Promise<void> => {
    if (features.length === 0) {
      setError('Please select at least one amenity');
      return;
    }
    dispatch(addAmenities(features));
    dispatch(addAmenitiesObject(allFeatures));

    await new Promise((resolve) => setTimeout(resolve, 200));

    try {
      const finalAmenities = getFinalAmenities();
      const finalPropertyDetails = {
        title: finalResult?.detail?.title,
        description: finalResult?.detail?.description,
        bnbUrl: finalResult?.detail?.bnbUrl,
        property3dUrl: finalResult?.detail?.property3dUrl,
        hostId: localStorage.getItem('userDetails')
          ? JSON.parse(localStorage.getItem('userDetails') || '{}').id
          : 0,
        propertyTypeId: finalResult?.place?.id,
        propertyTypeOther: finalResult?.place?.other || undefined,
        propertyOffering: {
          id: finalResult?.type?.id,
          description: finalResult?.subType?.name,
        },
        details: Object.keys(finalResult?.offerings).map((offering) => {
          return {
            propertyDetailsId: offering,
            detailsValue: finalResult?.offerings[offering],
          };
        }),
        location: {
          city: finalResult?.location?.city,
          state: finalResult?.location?.state,
          pincode: finalResult?.location?.pincode,
          addressLine1: finalResult?.location?.addressLine1,
          addressLine2: finalResult?.location?.addressLine2,
          landmark: finalResult?.location?.landmark,
          locality: finalResult?.location?.district,
          latitude: parseFloat(finalResult?.location?.coordinates?.lat),
          longitude: parseFloat(finalResult?.location?.coordinates?.lng),
        },
        amenities: finalAmenities,
        propertyMetadata: buildPropertyMetadata(features),
        onboardingStage: 1,
      };
      const res = await PropertyService.saveProperty(finalPropertyDetails);

      if (
        res.responseInformation &&
        (res.responseInformation as any)?.responseCode === 0
      ) {
        localStorage.setItem('newPropertId', res?.result?.propertyId);
        // The listing now exists on the server, so its draft is no longer needed.
        discardListingDraft();
        // The listing now exists on the server, so its draft is no longer needed.
        discardListingDraft();
      } else {
        notification.error({
          message: 'Unable to save amenities',
          description: getApiResponseMessage(
            res,
            'Could not save amenities. Please try again.',
          ),
        });
        return;
      }
      if (res.result && res.result.propertyId) {
        dispatch(setPropertyId(res.result.propertyId));
        dispatch(updateOnboardingStage(1));
        // Set businessType on the newly created property. Navigation is not
        // blocked on it, but a failure must be visible to the admin.
        // Admin-only: the endpoint 403s for hosts, and PropertyService's 403 handler signs the user
        // out. B2C is the DB default, so only B2B needs the call.
        if (isAdminUser() && businessType === 'B2B') {
          PropertyService.updateBusinessType(res.result.propertyId, businessType).catch(
            (err) => {
              console.error('Failed to set businessType', err);
              notification.error({
                message: 'Property type not saved',
                description: `The property was created as B2C. Set it to ${businessType} from the Property Source Dashboard.`,
              });
            },
          );
        }
        navigate('/host/property-photos');
      }
      if (hostPropertyId) {
        navigate(reviewPathAfterEdit(hostPropertyId));
        return;
      }
      navigate('/host/property-photos');
    } catch (e: any) {
      notification.error({
        message: 'Unable to save amenities',
        description: getUserFriendlyError(
          e,
          'Could not save amenities. Please try again.',
        ),
      });
      return;
    }
  };

  const followUpFlags = useMemo(() => amenitySelectionFlags(features), [features]);

  const poolAnchorName = useMemo(() => {
    const first = data.find(
      (t) => features.includes(t.name) && isPoolAmenityName(t.name),
    );
    return first?.name ?? null;
  }, [data, features]);

  const parkingAnchorName = useMemo(() => {
    const first = data.find(
      (t) => features.includes(t.name) && isParkingAmenityName(t.name),
    );
    return first?.name ?? null;
  }, [data, features]);

  const flexibleCheckinAnchorName = useMemo(() => {
    const first = data.find(
      (t) => features.includes(t.name) && isFlexibleCheckinAmenityName(t.name),
    );
    return first?.name ?? null;
  }, [data, features]);

  useEffect(() => {
    if (!followUpFlags.pool) {
      setFollowUpCollapsed((c) => ({ ...c, pool: false }));
    }
  }, [followUpFlags.pool]);

  useEffect(() => {
    if (!followUpFlags.parking) {
      setFollowUpCollapsed((c) => ({ ...c, parking: false }));
    }
  }, [followUpFlags.parking]);

  useEffect(() => {
    if (!followUpFlags.flexibleCheckin) {
      setFollowUpCollapsed((c) => ({ ...c, flexibleCheckin: false }));
    }
  }, [followUpFlags.flexibleCheckin]);

  const addItems = (amenity: AmenityData): void => {
    if (!features.includes(amenity.name)) {
      const nextFeatures = [...features, amenity.name];
      setFeatures(nextFeatures);
      setAllFeatures([...allFeatures, amenity]);
      setError('');
      const f = amenitySelectionFlags(nextFeatures);
      if (isPoolAmenityName(amenity.name) && f.pool) {
        setFollowUpCollapsed((c) => ({ ...c, pool: false }));
      } else if (isParkingAmenityName(amenity.name) && f.parking) {
        setFollowUpCollapsed((c) => ({ ...c, parking: false }));
      } else if (
        isFlexibleCheckinAmenityName(amenity.name) &&
        f.flexibleCheckin
      ) {
        setFollowUpCollapsed((c) => ({ ...c, flexibleCheckin: false }));
      }
      return;
    }

    const nextFeatures = features.filter((item) => item !== amenity.name);
    const nextAll = allFeatures.filter((item) => item.name !== amenity.name);
    setFeatures(nextFeatures);
    setAllFeatures(nextAll);

    const prevSel = amenitySelectionFlags(features);
    const nextSel = amenitySelectionFlags(nextFeatures);
    const clears: {
      poolLength?: string;
      poolBreadth?: string;
      poolDepth?: string;
      parkingDescription?: string;
      flexibleCheckinType?: '';
    } = {};

    if (prevSel.pool && !nextSel.pool) {
      clears.poolLength = '';
      clears.poolBreadth = '';
      clears.poolDepth = '';
    }
    if (prevSel.parking && !nextSel.parking) {
      clears.parkingDescription = '';
    }
    if (prevSel.flexibleCheckin && !nextSel.flexibleCheckin) {
      clears.flexibleCheckinType = '';
    }
    if (Object.keys(clears).length > 0) {
      dispatch(patchDetail(clears));
    }
  };

  const [amenitySearch, setAmenitySearch] = useState('');
  const searchTerm = amenitySearch.trim().toLowerCase();
  const visibleGroups = groupAmenities(data || [])
    .map((g) => ({
      ...g,
      items: searchTerm
        ? g.items.filter((item) => item.name.toLowerCase().includes(searchTerm))
        : g.items,
    }))
    .filter((g) => g.items.length > 0);

  const detail = finalResult.detail;
  const setSwitch = (
    key: 'elderlyFriendly' | 'childFriendly' | 'petFriendly' | 'securityGuard24x7',
    on: boolean,
  ): void => {
    dispatch(patchDetail({ [key]: on ? 'yes' : 'no' }));
  };

  // "None of these" is every flag answered "no"; the host must pick it or switch at least one on.
  // No pets chosen on the first screen: there is nothing to ask about pet friendliness.
  const hidePetFriendly = petsNotAllowed((finalResult as any).offerings, (finalResult as any).offeringsMap);
  const goodToKnowKeys = (
    hidePetFriendly
      ? ['elderlyFriendly', 'childFriendly', 'securityGuard24x7']
      : ['elderlyFriendly', 'childFriendly', 'petFriendly', 'securityGuard24x7']
  ) as ('elderlyFriendly' | 'childFriendly' | 'petFriendly' | 'securityGuard24x7')[];
  useEffect(() => {
    if (hidePetFriendly && finalResult.detail?.petFriendly === 'yes') {
      dispatch(patchDetail({ petFriendly: 'no' }));
    }
  }, [hidePetFriendly, finalResult.detail?.petFriendly, dispatch]);
  const noneSelected = goodToKnowKeys.every((k) => detail?.[k] === 'no');
  const anyGoodToKnow = goodToKnowKeys.some((k) => detail?.[k] === 'yes');
  const goodToKnowDone = noneSelected || anyGoodToKnow;
  const setNone = (on: boolean): void => {
    dispatch(
      patchDetail({
        elderlyFriendly: on ? 'no' : '',
        childFriendly: on ? 'no' : '',
        petFriendly: on ? 'no' : '',
        securityGuard24x7: on ? 'no' : '',
      }),
    );
  };

  // "Number of pets allowed" is stored as a regular property-detail count (if the backend offers one).
  const offeringsMapState = ((finalResult as any).offeringsMap || {}) as Record<string, any>;
  const petFieldId = Object.keys(offeringsMapState).find((k) => {
    const entry = offeringsMapState[k];
    const label = typeof entry === 'string' ? entry : entry?.detailsName || entry?.name || '';
    return /\bpets?\b/i.test(String(label));
  });
  const petCount = petFieldId ? Number(finalResult.offerings?.[petFieldId] || 0) : 0;

  const hasSelection = features.length > 0;
  const pendingItems = [
    !hasSelection && 'Select at least one amenity.',
    !goodToKnowDone && 'Under "Good to know", select at least one option or "None of these".',
  ].filter((item): item is string => typeof item === 'string');
  const isUpdate = isEdit && (hostPropertyId || onboardingStage >= 1);

  const renderFollowUp = (groupItems: AmenityData[]) => {
    const has = (anchor: string | null) => anchor !== null && groupItems.some((i) => i.name === anchor);
    return (
      <>
        {has(poolAnchorName) && followUpFlags.pool ? (
          followUpCollapsed.pool ? (
            <ReopenButton type="button" onClick={() => setFollowUpCollapsed((c) => ({ ...c, pool: false }))}>
              + Pool details
            </ReopenButton>
          ) : (
            <FollowUp role="group" aria-label="Pool size (optional)">
              <div className="top">
                <span className="title">Pool size (optional)</span>
                <button
                  type="button"
                  className="close"
                  aria-label="Dismiss"
                  onClick={() => setFollowUpCollapsed((c) => ({ ...c, pool: true }))}
                >
                  ×
                </button>
              </div>
              <div className="row">
                <div className="micro">
                  <span>L</span>
                  <input
                    id="poolLength"
                    value={detail?.poolLength || ''}
                    onChange={(e) => dispatch(patchDetail({ poolLength: e.target.value }))}
                    placeholder="Length"
                    aria-label="Pool length"
                  />
                </div>
                <div className="micro">
                  <span>W</span>
                  <input
                    id="poolBreadth"
                    value={detail?.poolBreadth || ''}
                    onChange={(e) => dispatch(patchDetail({ poolBreadth: e.target.value }))}
                    placeholder="Width"
                    aria-label="Pool width"
                  />
                </div>
                <div className="micro">
                  <span>D</span>
                  <input
                    id="poolDepth"
                    value={detail?.poolDepth || ''}
                    onChange={(e) => dispatch(patchDetail({ poolDepth: e.target.value }))}
                    placeholder="Depth"
                    aria-label="Pool depth"
                  />
                </div>
              </div>
            </FollowUp>
          )
        ) : null}

        {has(parkingAnchorName) && followUpFlags.parking ? (
          followUpCollapsed.parking ? (
            <ReopenButton type="button" onClick={() => setFollowUpCollapsed((c) => ({ ...c, parking: false }))}>
              + Parking details
            </ReopenButton>
          ) : (
            <FollowUp role="group" aria-label="Parking (optional)">
              <div className="top">
                <span className="title">Parking (optional)</span>
                <button
                  type="button"
                  className="close"
                  aria-label="Dismiss"
                  onClick={() => setFollowUpCollapsed((c) => ({ ...c, parking: true }))}
                >
                  ×
                </button>
              </div>
              <textarea
                id="parkingDesc"
                rows={3}
                value={detail?.parkingDescription || ''}
                onChange={(e) => dispatch(patchDetail({ parkingDescription: e.target.value }))}
                placeholder="exclusive/shared/FCFS/indoor"
                title="exclusive/shared/FCFS/indoor"
                aria-label="Parking details"
              />
            </FollowUp>
          )
        ) : null}

        {has(flexibleCheckinAnchorName) && followUpFlags.flexibleCheckin ? (
          followUpCollapsed.flexibleCheckin ? (
            <ReopenButton
              type="button"
              onClick={() => setFollowUpCollapsed((c) => ({ ...c, flexibleCheckin: false }))}
            >
              + Check-in type
            </ReopenButton>
          ) : (
            <FollowUp role="group" aria-label="Check-in type (optional)">
              <div className="top">
                <span className="title">Check-in type (optional)</span>
                <button
                  type="button"
                  className="close"
                  aria-label="Dismiss"
                  onClick={() => setFollowUpCollapsed((c) => ({ ...c, flexibleCheckin: true }))}
                >
                  ×
                </button>
              </div>
              <div className="labeled">
                <span>Type</span>
                <select
                  id="flexibleCheckinType"
                  value={detail?.flexibleCheckinType || ''}
                  onChange={(e) =>
                    dispatch(
                      patchDetail({
                        flexibleCheckinType: e.target.value as FlexibleCheckinFormValue,
                      }),
                    )
                  }
                  aria-label="Check-in type"
                >
                  <option value="">—</option>
                  <option value="self">Self check-in</option>
                  <option value="assisted">Assisted</option>
                  <option value="front_desk">Front desk</option>
                  <option value="digital">Digital / keyless</option>
                </select>
              </div>
            </FollowUp>
          )
        ) : null}
      </>
    );
  };

  return (
    <OnboardingShell
      section={3}
      title="Select all the amenities you can offer"
      wide
      onBack={backHandler}
      onContinue={isUpdate ? updateHandler : nextHandler}
      continueLabel={isUpdate ? 'Update' : 'Continue'}
      continueDisabled={!hasSelection || !goodToKnowDone}
      pending={pendingItems}
      modal={BackConfirmModal}
    >
      <Layout>
        <MainCol>
          <SearchWrap>
            <FiSearch size={16} />
            <input
              value={amenitySearch}
              onChange={(e) => setAmenitySearch(e.target.value)}
              placeholder="Search amenities"
              aria-label="Search amenities"
            />
          </SearchWrap>

          {visibleGroups.map(({ group, items }) => {
            const count = items.filter((item) => features.includes(item.name)).length;
            return (
              <GroupCard key={group}>
                <div className="head">
                  <h3>{group}</h3>
                  {count > 0 ? <span className="count">{count} selected</span> : null}
                </div>
                <div className="chips">
                  {items.map((item) => {
                    const selected = features.includes(item.name);
                    const AmenityIcon = getAmenityIcon(item.name);
                    return (
                      <AmenityChip
                        key={item.id ?? item.name}
                        type="button"
                        $selected={selected}
                        aria-pressed={selected}
                        onClick={() => addItems(item)}
                      >
                        {selected ? (
                          <FiCheck size={14} strokeWidth={3} />
                        ) : AmenityIcon ? (
                          <AmenityIcon size={14} color="#6b6b6b" />
                        ) : (
                          <img
                            src={`/images/amenities/${getIcon(item)}.png`}
                            alt=""
                            onError={(e) => {
                              const img = e.currentTarget;
                              if (img.src.endsWith('/images/amenities/fallback.png')) return;
                              img.src = '/images/amenities/fallback.png';
                            }}
                          />
                        )}
                        {amenityLabel(item.name)}
                      </AmenityChip>
                    );
                  })}
                </div>
                {renderFollowUp(items)}
              </GroupCard>
            );
          })}

          {data.length === 0 && amenitiesFailed ? (
            <ErrorText as="p" role="alert" style={{ fontWeight: 400 }}>
              We couldn’t load the amenities list.{' '}
              <button
                type="button"
                onClick={() => setAmenitiesReload((n) => n + 1)}
                style={{
                  border: 0,
                  background: 'none',
                  padding: 0,
                  color: '#f1662f',
                  fontWeight: 600,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                Try again
              </button>
            </ErrorText>
          ) : data.length === 0 ? (
            <p style={{ margin: 0, fontSize: 14, color: '#6b6b6b' }}>Loading amenities…</p>
          ) : visibleGroups.length === 0 ? (
            <ErrorText as="p" style={{ color: '#6b6b6b', fontWeight: 400 }}>
              No amenity matches “{amenitySearch}”.
            </ErrorText>
          ) : null}
          {!hasSelection ? <RequiredChoice message="Select at least one amenity." /> : null}
          {error && <ErrorText role="alert">{error}</ErrorText>}
        </MainCol>

        <Side>
          <Group
            title="Good to know"
            note={'Select at least one. If none of these apply, choose "None of these".'}
          >
            <SwitchStack>
              <SwitchRow
                label="Elderly friendly"
                info="Typically includes wheelchair accessibility, a ground-floor bedroom, or a private lift."
                checked={detail?.elderlyFriendly === 'yes'}
                onChange={(on) => setSwitch('elderlyFriendly', on)}
              />
              <SwitchRow
                label="Child friendly"
                info="Suitable for families with children, with child-friendly spaces and amenities such as a baby cot and high chair."
                checked={detail?.childFriendly === 'yes'}
                onChange={(on) => setSwitch('childFriendly', on)}
              />
              {hidePetFriendly ? null : (
                <SwitchRow
                  label="Pet friendly"
                  checked={detail?.petFriendly === 'yes'}
                  onChange={(on) => setSwitch('petFriendly', on)}
                />
              )}
              <SwitchRow
                label="24×7 security"
                checked={detail?.securityGuard24x7 === 'yes'}
                onChange={(on) => setSwitch('securityGuard24x7', on)}
              />
              <SwitchRow label="None of these" checked={noneSelected} onChange={setNone} />
            </SwitchStack>
            {!goodToKnowDone ? (
              <RequiredChoice message="Select at least one option, or “None of these”." />
            ) : null}
            {detail?.petFriendly === 'yes' && petFieldId ? (
              <Counter
                label="Number of pets allowed"
                value={petCount}
                onChange={(next) =>
                  dispatch(
                    addOfferings({
                      ...(finalResult.offerings || {}),
                      [petFieldId]: Math.max(0, next),
                    }),
                  )
                }
              />
            ) : null}
          </Group>
        </Side>
      </Layout>
    </OnboardingShell>
  );
};

export default Amenities;